import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const fillPreview = (tpl, t) => {
  const src = (t.has_link ? tpl : tpl.replaceAll("{link} or call", "call")).replace(/\{\{\s*FirstName\s*\}\}|\{first name\}/gi, t.sample?.first || "");
  const out = src.replace(/\{(\w+)\}/g, (m, k) => (k in (t.sample || {}) ? String(t.sample[k]) : m)).replace(/[ \t]{2,}/g, " ").trim();
  return out && t.footer ? `${out}\n${t.footer}` : out;
};

export const AutoTextEditor = ({ t, onDone }) => {
  const qc = useQueryClient();
  const [tpl, setTpl] = useState(t.template);
  const ref = useRef(null);
  const insert = (tag) => {
    const el = ref.current;
    const s = el ? el.selectionStart : tpl.length;
    const e = el ? el.selectionEnd : tpl.length;
    setTpl(tpl.slice(0, s) + tag + tpl.slice(e));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(s + tag.length, s + tag.length); });
  };
  const done = (msg) => { toast.success(msg); qc.invalidateQueries({ queryKey: ["automatic-texts"] }); qc.invalidateQueries({ queryKey: ["birthdays"] }); onDone(); };
  const fail = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
  const save = useMutation({ mutationFn: () => api.put(`/automatic-texts/${t.key}`, { template: tpl }), onSuccess: () => done(`${t.name} saved`), onError: fail });
  const reset = useMutation({ mutationFn: () => api.delete(`/automatic-texts/${t.key}`), onSuccess: () => done(`${t.name} reset to the original wording`), onError: fail });

  return (
    <div className="mt-2 space-y-2" data-testid={`auto-text-editor-${t.key}`}>
      <Textarea ref={ref} rows={4} value={tpl} onChange={(e) => setTpl(e.target.value)} maxLength={480} data-testid={`auto-text-input-${t.key}`} />
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-slate-500">Insert:</span>
        {t.tags.map(({ tag, help, insert: raw }) => (
          <button key={tag} type="button" onClick={() => insert(raw || `{${tag}}`)} title={help} data-testid={`auto-text-tag-${t.key}-${tag}`}
            className="rounded-full border border-slate-200 bg-white px-2 py-0.5 font-mono text-[11px] text-slate-700 hover:bg-slate-50">{raw || `{${tag}}`}</button>))}
        <span className="ml-auto text-[11px] text-slate-400">{[...tpl].length}/480</span>
      </div>
      <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-2" data-testid={`auto-text-live-preview-${t.key}`}>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Live preview · what the patient sees</p>
        <p className="mt-1 whitespace-pre-wrap break-words text-xs text-slate-800" data-testid={`auto-text-live-preview-text-${t.key}`}>
          {fillPreview(tpl, t) || <span className="italic text-slate-400">Start typing to see the text</span>}
        </p>
      </div>
      <p className="text-[11px] text-slate-500">Tags are filled in with each patient's details. {t.footer ? `"${t.footer}" is added automatically.` : 'Keep "Reply STOP to opt out." where it\'s required.'}</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="bg-[#0B1F44] hover:bg-[#13306a]" disabled={save.isPending} onClick={() => save.mutate()} data-testid={`auto-text-save-${t.key}`}>Save</Button>
        <Button size="sm" variant="outline" onClick={onDone} data-testid={`auto-text-cancel-${t.key}`}>Cancel</Button>
        {t.edited && <Button size="sm" variant="ghost" className="text-slate-600" disabled={reset.isPending}
          onClick={() => window.confirm("Go back to the original wording?") && reset.mutate()} data-testid={`auto-text-reset-${t.key}`}>Reset to original</Button>}
      </div>
    </div>
  );
};
